"use client";

import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import type { DialogProps } from "@/components/controls/base-controls";
import {
	AppBar,
	Box,
	Button,
	Divider,
	IconButton,
	Image,
	Stack,
	Toolbar,
	Typography,
	useMediaQuery,
	useTheme
} from "@/components/controls/base-controls";
import { MenuIcon } from "@/components/controls/icons";
import AppsDialog from "@/components/dialogs/apps.dialog";
import LocaleSelect from "@/components/locale/locale-select";
import SignInPopover from "@/components/popovers/sign-in.popover";
import {
	HEADER_HEIGHT_FULL,
	HEADER_HEIGHT_MOBILE,
	WEBPAGE_CORE_CONTAINER
} from "@/core/constants/webpages";
import { getAppModules } from "@/core/providers/app-modules";
import { SignIn } from "@/core/types/sign-in.type";
import { Link } from "@/i18n/navigation";

const MainHeader = () => {
	const t = useTranslations();

	const appModules = getAppModules();

	const theme = useTheme();
    const isMobileQuery = useMediaQuery(theme.breakpoints.down('sm'));
	const [isMobile, setIsMobile] = useState(false);

	useEffect(() => {
		setIsMobile(isMobileQuery);
	}, [isMobileQuery]);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogScroll, setDialogScroll] =
		useState<DialogProps["scroll"]>("paper");

	const pathname = usePathname();
	const isActive = (href: string) => pathname === href || pathname.endsWith(`${href}`);

	const [signInAnchorEl, setSignInAnchorEl] = useState<HTMLButtonElement | null>(null);
	const openSignInPopover = Boolean(signInAnchorEl);

	const handleDialogClose = () => {
		try {
			setDialogOpen(() => false);
		} catch (error) {
			console.error("Error closing apps dialog:", error);
		}
	};

	const handleDialogOpen = (scrollType: DialogProps["scroll"]) => () => {
		try {
			setDialogOpen(() => true);
			setDialogScroll(() => scrollType);
		} catch (error) {
			console.error("Error opening apps dialog:", error);
		}
	};

	const handleSignInBtnClick = (e: React.MouseEvent<HTMLButtonElement>) => setSignInAnchorEl(e.currentTarget);

	return (
		<>
			<AppBar
				sx={{
					position: isMobile ? "relative" : "fixed",
					backgroundColor: "var(--mui-palette-header-bg)",
					top: 0,
					left: 0,
					right: 0,
					zIndex: 10,
					px: 0,
					overflow: "hidden"
				}}
			>
				<Toolbar
					disableGutters
					sx={{
						display: "flex",
						flexDirection: "row",
						height: { xs: HEADER_HEIGHT_MOBILE, md: HEADER_HEIGHT_FULL },
						justifyContent: "space-between",
						alignItems: "center",
						margin: "0 auto",
						padding: {xs: "0 .5em 0 1em", md: "0 1em"},
						width: WEBPAGE_CORE_CONTAINER,
						maxWidth: "100%",
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: { xs: 1, md: 2 },
						}}
					>
						<Image
							src="/images/logo.png"
							alt="Navairra Logo"
							loading="eager"
							width={isMobileQuery ? 44 : 66}
							height={isMobileQuery ? 31 : 47}
							style={{
								filter: "brightness(0) invert(1)",
							}}
						/>
						
						<Link
							href="/home"
							style={{
								textDecoration: "none",
								whiteSpace: "pre-line",
								color: "var(--mui-palette-link-highlight)",
							}}
						>
							<Typography
								variant="companyName"
								sx={{
									color: "var(--mui-palette-lightest)",
								}}
							>
								{t("company.name")}
							</Typography>
						</Link>

						<Divider
							orientation="vertical"
							flexItem
							sx={{
								borderColor: "var(--mui-palette-dull)",
								height: "2em",
								borderRightWidth: "2px",
								my: "auto",
							}}
						/>

						<Typography
							variant="tagLine"
							sx={{
								color: "var(--mui-palette-light)",
								mt: { xs: "4px", md: "3px" },
							}}
						>
							{t("company.tagLine1")}
						</Typography>
					</Box>

					<Stack sx={{
						display: "flex",
						flexDirection: "row",
						justifyContent: {xs: "flex-end", md: "space-between"},
						alignItems: "center",
						pr: {xs: 1.5, md: 0},
						gap: { xs: 1, md: 1.5 },
					}}>

						<Box sx={{
							display: {xs: "none", md: "flex" }, 
							alignItems:"center", 
							columnGap:2
						}}>
							{appModules.map((module) => (
								(String(module.path) !== "/home" && !String(module.path).endsWith("sig")) && <Link
									key={module.id}
									href={module.path}
									style={{
										textDecoration: "none",
										borderBottom: isActive(module.path)
											? "2px solid var(--mui-palette-link-highlight)"
											: "none",
										paddingBottom: "3px",
										fontSize: ".875rem",
										minWidth: "4em",
										textAlign: "center",
										whiteSpace: "pre-line",
										color: "var(--mui-palette-link-highlight)",
									}}
								>
									{t(module.label)}
								</Link>
							))}

							<LocaleSelect />

						</Box>

						<Button type="button" 
							variant="square" 
                    		onClick={handleSignInBtnClick}
							sx={{ 
								border: "1px solid silver",
								display: {xs: "none", md: "block"},
								minWidth: "5em",
								textTransform: "none",
							}}> 
							<Typography sx={{
								color: "var(--mui-palette-link)",
								fontSize: ".875em"}}> 
								{t("sign_in.label")} 
							</Typography> 
						</Button>

					</Stack>

					<IconButton
						color="inherit"
						aria-label="open drawer"
						edge="start"
						onClick={handleDialogOpen("paper")}
						sx={{ mr: -0.3, display: { md: "none" } }}
					>
						<MenuIcon />
					</IconButton>
				</Toolbar>
			</AppBar>

			<AppsDialog
				appModules={appModules}
				open={dialogOpen}
				onHandleDialogClose={handleDialogClose}
				scroll={dialogScroll}
			/>

			<SignInPopover
				allowedValues={Object.values(SignIn)}
				anchorEl={signInAnchorEl}
				open={openSignInPopover}
				onClose={() => setSignInAnchorEl(null)}
			/>
		</>
	);
};

export default MainHeader;
